import { useState, useMemo } from 'react';
import {
  ArrowLeft, CheckCircle, XCircle, MessageSquare, Send, Shield, Clock,
  ChevronDown, ChevronUp, Edit3, AlertTriangle, History, FileText, Eye, Plus,
  Sparkles, Download, GitCompare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import API from '@/lib/api';
import { generatePlainSummary } from './ProposalBuilder';
import CampaignBriefCard from '@/components/CampaignBriefCard';
import AgencyProfileModal from '@/components/AgencyProfileModal';
import ProposalDiffViewer from '@/components/ProposalDiffViewer';

// ── Sensitivity classifiers
const HIGH_SENSITIVITY = [
  { key: 'skin_tone', label: 'Skin tone adjustments' },
  { key: 'body_composition', label: 'Body composition changes' },
  { key: 'facial_features', label: 'Facial feature alterations' },
  { key: 'age_alteration', label: 'Age alteration' },
  { key: 'gender_presentation', label: 'Gender presentation changes' },
];

// ── Reviewable clauses with friendly labels & current-value extractors, grouped by section
const CLAUSE_SECTIONS = [
  {
    section: 'Commercial Offer',
    clauses: [
      { key: 'commercial_offer.deal_structure',         label: 'Deal Structure',                   get: (p) => p?.commercial_offer?.deal_structure },
      { key: 'commercial_offer.base_amount',            label: 'Base Amount',                      get: (p) => p?.commercial_offer?.base_amount || p?.commercial_offer?.amount || p?.offered_price, format: (v, p) => v ? `${p?.commercial_offer?.currency || 'USD'} ${Number(v).toLocaleString()}` : v },
      { key: 'commercial_offer.recurring_amount',       label: 'Recurring Fee',                    get: (p) => p?.commercial_offer?.recurring_amount, format: (v, p) => v ? `${p?.commercial_offer?.currency || 'USD'} ${Number(v).toLocaleString()}` : v },
      { key: 'commercial_offer.signon_bonus_amount',    label: 'Sign-On Bonus',                    get: (p) => p?.commercial_offer?.signon_bonus_amount, format: (v, p) => v ? `${p?.commercial_offer?.signon_bonus_currency || 'USD'} ${Number(v).toLocaleString()} (${(p?.commercial_offer?.signon_bonus_timing || 'on signing').replace(/_/g, ' ')})` : v },
      { key: 'commercial_offer.payment_model',          label: 'Payment Model',                    get: (p) => p?.commercial_offer?.payment_model },
      { key: 'commercial_offer.response_deadline',      label: 'Response Deadline',                get: (p) => p?.commercial_offer?.response_deadline },
    ],
  },
  {
    section: 'License Scope',
    clauses: [
      { key: 'rights_terms.exclusivity',                label: 'Exclusivity',                      get: (p) => p?.rights_terms?.exclusivity },
      { key: 'rights_terms.exclusivity_categories',     label: 'Exclusive Categories',             get: (p) => p?.rights_terms?.exclusivity_categories?.join(', ') },
      { key: 'rights_terms.territory',                  label: 'Territory',                        get: (p) => p?.rights_terms?.territory?.join(', ') },
      { key: 'rights_terms.duration_type',              label: 'Duration',                         get: (p) => p?.rights_terms?.duration_type },
      { key: 'rights_terms.term_start',                 label: 'Term Start',                       get: (p) => p?.rights_terms?.term_start },
      { key: 'rights_terms.term_end',                   label: 'Term End',                         get: (p) => p?.rights_terms?.term_end },
      { key: 'rights_terms.expiry_behavior',            label: 'Expiry Behavior',                  get: (p) => p?.rights_terms?.expiry_behavior },
      { key: 'rights_terms.sublicensing',               label: 'Sublicensing',                     get: (p) => p?.rights_terms?.sublicensing },
    ],
  },
  {
    section: 'AI Generation Permissions',
    clauses: [
      { key: 'rights_terms.generation_types',           label: 'Permitted Generation Types',       get: (p) => p?.rights_terms?.generation_types?.join(', ') },
      { key: 'rights_terms.visual_representation',      label: 'Visual Representation',            get: (p) => p?.rights_terms?.visual_representation },
      { key: 'rights_terms.permitted_alterations',      label: 'Permitted Alterations',            get: (p) => p?.rights_terms?.permitted_alterations?.join(', ') },
      { key: 'rights_terms.dialogue_permitted',         label: 'Voice Synthesis',                  get: (p) => p?.rights_terms?.dialogue_permitted ? `Permitted${p?.rights_terms?.dialogue_scripted_only ? ' (scripted only)' : ''}` : 'Not permitted' },
      { key: 'rights_terms.statements_permitted',       label: 'Creator Statements',               get: (p) => p?.rights_terms?.dialogue_permitted ? (p?.rights_terms?.statements_permitted ? `Permitted, scope: ${p?.rights_terms?.statement_scope || '—'}` : 'Not permitted') : null },
      { key: 'rights_terms.post_generation_modifications', label: 'Post-Gen Modifications',        get: (p) => p?.rights_terms?.post_generation_modifications?.includes('none') ? 'None — must be used as-is' : p?.rights_terms?.post_generation_modifications?.join(', ') },
      { key: 'rights_terms.ai_model_data_usage_prohibited', label: 'AI Model Training',           get: () => 'Prohibited (platform commitment)' },
    ],
  },
  {
    section: 'Permitted Use & Restrictions',
    clauses: [
      { key: 'rights_terms.purpose_of_use',             label: 'Purpose of Use',                   get: (p) => p?.rights_terms?.purpose_of_use?.join(', ') },
      { key: 'rights_terms.usage_scope',                label: 'Usage Scope',                      get: (p) => p?.rights_terms?.usage_scope },
      { key: 'rights_terms.content_restrictions',      label: 'Content Restrictions',              get: (p) => `${p?.rights_terms?.content_restrictions?.length || 0} categories restricted` },
      { key: 'rights_terms.competitor_restriction',     label: 'Competitor Restriction',           get: (p) => p?.rights_terms?.competitor_restriction ? (p?.rights_terms?.competitor_names || 'Yes') : 'None' },
      { key: 'rights_terms.output_reuse',               label: 'Output Reuse After Term',          get: (p) => p?.rights_terms?.output_reuse },
      { key: 'rights_terms.source_material_reuse',      label: 'Source Material Reuse',            get: (p) => p?.rights_terms?.source_material_reuse },
      { key: 'rights_terms.endorsement_requested',     label: 'Endorsement',                       get: (p) => p?.rights_terms?.endorsement_requested ? 'Requested' : 'Not requested' },
    ],
  },
  {
    section: 'Asset Ownership',
    clauses: [
      { key: 'rights_terms.asset_ownership',            label: 'Ownership of Generated Assets',    get: (p) => p?.rights_terms?.asset_ownership },
      { key: 'rights_terms.creator_portfolio_rights',   label: 'Creator Portfolio Rights',         get: (p) => p?.rights_terms?.creator_portfolio_rights ? 'Yes' : 'No' },
      { key: 'rights_terms.moral_rights',               label: 'Moral Rights',                     get: (p) => p?.rights_terms?.moral_rights },
    ],
  },
  {
    section: 'Subscription Terms',
    showWhen: (p) => ['subscription', 'hybrid'].includes(p?.commercial_offer?.deal_structure),
    clauses: [
      { key: 'rights_terms.subscription_billing_frequency', label: 'Billing Frequency',           get: (p) => p?.rights_terms?.subscription_billing_frequency },
      { key: 'rights_terms.generation_caps_enabled',    label: 'Generation Caps',                  get: (p) => p?.rights_terms?.generation_caps_enabled ? 'Per-period caps applied' : 'No per-period caps' },
      { key: 'rights_terms.content_reuse_scope',        label: 'Content Reuse Scope',              get: (p) => p?.rights_terms?.content_reuse_scope },
      { key: 'rights_terms.subscription_exclusivity',   label: 'Subscription Exclusivity',         get: (p) => p?.rights_terms?.subscription_exclusivity },
      { key: 'rights_terms.suspension_permitted',       label: 'Suspension',                       get: (p) => p?.rights_terms?.suspension_permitted ? `Up to ${p?.rights_terms?.suspension_max_days || 0} days / ${(p?.rights_terms?.suspension_period_basis || 'period').replace(/_/g, ' ')}` : 'Not permitted' },
      { key: 'rights_terms.price_change_policy',        label: 'Price Change Policy',              get: (p) => p?.rights_terms?.price_change_policy },
      { key: 'rights_terms.agency_cancellation_notice_days', label: 'Agency Cancellation Notice', get: (p) => p?.rights_terms?.agency_cancellation_notice_days ? `${p?.rights_terms?.agency_cancellation_notice_days} days` : null },
      { key: 'rights_terms.agency_cancellation_fee',    label: 'Agency Cancellation Fee',          get: (p) => p?.rights_terms?.agency_cancellation_fee },
      { key: 'rights_terms.creator_withdrawal_notice_days', label: 'Your Withdrawal Notice',      get: (p) => p?.rights_terms?.creator_withdrawal_notice_days ? `${p?.rights_terms?.creator_withdrawal_notice_days} days` : null },
      { key: 'rights_terms.accumulated_assets_post_subscription', label: 'Assets After Subscription Ends', get: (p) => p?.rights_terms?.accumulated_assets_post_subscription },
    ],
  },
  {
    section: 'Termination & Expiry',
    clauses: [
      { key: 'rights_terms.termination_triggers',       label: 'Termination Triggers',             get: (p) => (p?.rights_terms?.termination_triggers || []).length ? `${p.rights_terms.termination_triggers.length} conditions` : null },
      { key: 'rights_terms.cure_period',                label: 'Cure Period',                      get: (p) => p?.rights_terms?.cure_period },
      { key: 'rights_terms.morality_clause',            label: 'Morality Clause',                  get: (p) => p?.rights_terms?.morality_clause ? `Active (${(p?.rights_terms?.morality_conditions || []).length} conditions)` : 'None' },
      { key: 'rights_terms.early_termination_permitted', label: 'Early Termination by Agency',     get: (p) => p?.rights_terms?.early_termination_permitted ? `${p?.rights_terms?.early_termination_notice_days || 0} days notice / ${p?.rights_terms?.early_termination_compensation || '—'}${p?.rights_terms?.early_termination_compensation === 'kill_fee' ? ` (${p?.rights_terms?.early_termination_kill_fee_percentage || 0}%)` : ''}` : 'Not permitted' },
      { key: 'rights_terms.post_termination_takedown_days', label: 'Takedown Window',             get: (p) => p?.rights_terms?.post_termination_takedown_days ? `${p?.rights_terms?.post_termination_takedown_days} days` : null },
      { key: 'rights_terms.post_termination_deletion_days', label: 'Deletion Window',             get: (p) => p?.rights_terms?.post_termination_deletion_days ? `${p?.rights_terms?.post_termination_deletion_days} days` : null },
      { key: 'rights_terms.residual_use_permitted',     label: 'Residual Use',                     get: (p) => p?.rights_terms?.residual_use_permitted ? `${p?.rights_terms?.residual_use_days || 0} days, ${(p?.rights_terms?.residual_use_channels || []).join(', ')}` : 'Not permitted' },
      { key: 'rights_terms.deletion_confirmation_required', label: 'Deletion Confirmation',        get: (p) => p?.rights_terms?.deletion_confirmation_required ? 'Required' : 'Not required' },
      { key: 'rights_terms.governing_law',              label: 'Governing Law',                    get: (p) => p?.rights_terms?.governing_law },
      { key: 'rights_terms.dispute_resolution',         label: 'Dispute Resolution',               get: (p) => p?.rights_terms?.dispute_resolution },
    ],
  },
  {
    section: 'Likeness Pack Request',
    clauses: [
      { key: 'likeness_request.standard_pack_included', label: 'Standard Pack',                    get: (p) => p?.likeness_request?.standard_pack_included !== false ? 'Included' : 'Not requested' },
      { key: 'likeness_request.additional_requirements', label: 'Additional Requirements',         get: (p) => (p?.likeness_request?.additional_requirements || []).join(', ') },
      { key: 'likeness_request.custom_notes',           label: 'Custom Notes',                     get: (p) => p?.likeness_request?.custom_notes },
    ],
  },
];

function StatusBadge({ status }) {
  const cls = {
    sent: 'bg-blue-50 text-blue-700 border-blue-200',
    accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
    countered: 'bg-purple-50 text-purple-700 border-purple-200',
  }[status] || 'bg-slate-50 text-slate-500 border-slate-200';
  return <Badge variant="outline" className={`text-xs capitalize ${cls}`}>{(status || '').replace(/_/g, ' ')}</Badge>;
}

export default function CreatorProposalDetail({ deal, onBack, onRefresh }) {
  const proposal = deal.proposal || {};
  const co = proposal.commercial_offer || {};
  const rt = proposal.rights_terms || {};
  const cb = proposal.campaign_brief || {};
  const lr = proposal.likeness_request || {};

  const headlineAmount = co.base_amount || co.amount || proposal.offered_price || 0;
  const recurring = co.recurring_amount || 0;
  const bonus = co.signon_bonus_amount || 0;

  const [showFullTerms, setShowFullTerms] = useState(false);
  const [requestChangeOpen, setRequestChangeOpen] = useState(false);
  const [activeClause, setActiveClause] = useState(null);
  const [pendingObjections, setPendingObjections] = useState([]);
  const [counterOpen, setCounterOpen] = useState(false);
  const [counterPrice, setCounterPrice] = useState('');
  const [counterMessage, setCounterMessage] = useState('');
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [acks, setAcks] = useState({});
  const [responding, setResponding] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [showAgencyModal, setShowAgencyModal] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [diffViewerOpen, setDiffViewerOpen] = useState(false);

  const canRespond = deal.proposal_status === 'sent' || deal.proposal_status === 'countered';
  const canExport = ['sent', 'countered', 'accepted'].includes(deal.proposal_status);
  const versions = deal.proposal_versions || [];

  const downloadAgreementPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await API.get(`/deals/${deal.id}/agreement.pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const cd = res.headers?.['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/i.exec(cd);
      link.download = match?.[1] || `mylikeness-agreement-${deal.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Agreement downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Acknowledgments needed at accept-time
  const requiredAcks = useMemo(() => {
    const alts = rt.permitted_alterations || [];
    return HIGH_SENSITIVITY.filter(s => alts.includes(s.key));
  }, [rt]);

  // Filter sections to those visible (some only show on certain deal_structures)
  const visibleSections = CLAUSE_SECTIONS.filter(s => !s.showWhen || s.showWhen(proposal));

  // ── Plain-language summary (AI-written when available)
  const summary = useMemo(() => {
    return rt.ai_summary || rt.plain_language_summary || generatePlainSummary({
      brief: cb,
      offer: co,
      rights: rt,
    });
  }, [rt, co, cb]);

  // ── Open Request Change dialog for a clause
  const openRequestChange = (clause) => {
    setActiveClause({ ...clause, current_value: clause.get(proposal), requested_value: '', note: '' });
    setRequestChangeOpen(true);
  };

  const submitClauseChange = () => {
    if (!activeClause?.requested_value && !activeClause?.note) {
      toast.error('Add a requested change or note first.');
      return;
    }
    setPendingObjections([...pendingObjections, {
      clause_path: activeClause.key,
      clause_label: activeClause.label,
      current_value: activeClause.current_value,
      requested_value: activeClause.requested_value,
      note: activeClause.note,
    }]);
    setRequestChangeOpen(false);
    setActiveClause(null);
    toast.success('Change request added — review & send all together.');
  };

  // ── Send counter with all pending objections
  const sendCounter = async () => {
    if (pendingObjections.length === 0 && !counterMessage) {
      toast.error('Add at least one change request or message.');
      return;
    }
    setResponding(true);
    try {
      await API.put(`/creator/proposals/${deal.id}/respond`, {
        action: 'counter',
        counter_price: parseFloat(counterPrice) || null,
        message: counterMessage,
        clause_objections: pendingObjections,
      });
      toast.success('Counter sent to agency');
      setCounterOpen(false);
      setPendingObjections([]);
      setCounterPrice(''); setCounterMessage('');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setResponding(false); }
  };

  // ── Accept (with required ack check)
  const startAccept = () => {
    if (requiredAcks.length === 0) {
      doAccept([]);
      return;
    }
    setAcks(Object.fromEntries(requiredAcks.map(a => [a.key, false])));
    setAcceptOpen(true);
  };

  const doAccept = async (ackKeys) => {
    setResponding(true);
    try {
      await API.put(`/creator/proposals/${deal.id}/respond`, {
        action: 'accept',
        acceptance_acknowledgments: ackKeys,
      });
      toast.success('Proposal accepted! License created.');
      setAcceptOpen(false);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setResponding(false); }
  };

  const allAcksChecked = requiredAcks.every(a => acks[a.key]);

  const reject = async () => {
    if (!window.confirm('Decline this proposal?')) return;
    setResponding(true);
    try {
      await API.put(`/creator/proposals/${deal.id}/respond`, { action: 'reject' });
      toast.success('Proposal declined');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setResponding(false); }
  };

  return (
    <div data-testid="creator-proposal-detail">
      <div className="flex items-center justify-between mb-5">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> Back to Proposals
        </button>
        <div className="flex items-center gap-2">
          {versions.length >= 2 && (
            <Button variant="outline" size="sm" onClick={() => setDiffViewerOpen(true)}
              data-testid="open-version-diff-btn"
              className="text-xs border-slate-200 text-slate-700 hover:bg-slate-50">
              <GitCompare className="w-3.5 h-3.5 mr-1.5" /> Compare versions
            </Button>
          )}
          {canExport && (
            <Button variant="outline" size="sm" onClick={downloadAgreementPdf} disabled={downloadingPdf}
              data-testid="download-agreement-pdf-btn"
              className="text-xs border-blue-200 text-blue-700 hover:bg-blue-50">
              <Download className="w-3.5 h-3.5 mr-1.5" />
              {downloadingPdf ? 'Preparing…' : 'Download PDF'}
            </Button>
          )}
        </div>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold text-slate-900 truncate">{deal.campaign?.brand || deal.campaign?.name || 'Proposal'}</h2>
            <StatusBadge status={deal.proposal_status} />
          </div>
          <p className="text-slate-500 mt-1 text-sm">
            From{' '}
            <button
              type="button"
              onClick={() => setShowAgencyModal(true)}
              data-testid="agency-name-link"
              className="font-medium text-slate-700 hover:text-blue-600 hover:underline transition-colors"
            >
              {deal.agency?.name}
            </button>
          </p>
        </div>
        <div className="text-right shrink-0 ml-4">
          <p className="text-3xl font-semibold text-blue-600">${Number(headlineAmount).toLocaleString()}</p>
          <p className="text-slate-500 text-sm capitalize">
            {co.deal_structure === 'subscription' ? `${co.currency || 'USD'} ${recurring.toLocaleString()} ${(rt.subscription_billing_frequency || 'monthly')}` :
             co.deal_structure === 'hybrid' ? `Base + ${co.currency || 'USD'} ${recurring.toLocaleString()} recurring` :
             (co.payment_model?.replace(/_/g, ' ') || 'One-time')}
          </p>
          {bonus > 0 && <p className="text-amber-600 text-xs mt-0.5">+ ${bonus.toLocaleString()} sign-on bonus</p>}
        </div>
      </div>

      {/* Campaign Brief — what is this campaign for? */}
      <CampaignBriefCard campaign={deal.campaign} />

      {/* Agency profile modal */}
      <AgencyProfileModal
        open={showAgencyModal}
        onClose={() => setShowAgencyModal(false)}
        agencyId={deal.agency_id}
        fallbackAgency={deal.agency}
      />

      {/* Plain-language summary card (PRIMARY VIEW) */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-5" data-testid="plain-summary-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-500" /> Proposal Summary
            {rt.ai_summary && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-violet-600 bg-violet-50 border border-violet-100 px-1.5 py-0.5 rounded-full" data-testid="creator-ai-summary-badge">
                <Sparkles className="w-3 h-3" /> AI
              </span>
            )}
          </h3>
          <Button variant="ghost" size="sm" onClick={() => setShowFullTerms(!showFullTerms)}
            data-testid="toggle-full-terms" className="text-xs text-blue-600 hover:bg-blue-50 h-7">
            <Eye className="w-3 h-3 mr-1" />{showFullTerms ? 'Hide' : 'View'} Full Terms
          </Button>
        </div>
        <div className="prose prose-sm text-slate-700 max-w-none whitespace-pre-line leading-relaxed">
          {summary}
        </div>
      </div>

      {/* Full terms — clause-by-clause grouped by section, with Request Change buttons */}
      {showFullTerms && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-5" data-testid="full-terms-block">
          <h3 className="text-base font-semibold text-slate-800 mb-1 flex items-center gap-2">
            <Shield className="w-4 h-4 text-purple-500" /> Full Licensing Agreement
          </h3>
          <p className="text-[11px] text-slate-500 mb-4">
            Every term the agency has set (or left blank). Click <strong>Request Change</strong> next to any clause you'd like revised.
            Multiple change requests are bundled into a single counter.
          </p>
          <div className="space-y-5">
            {visibleSections.map(sec => (
              <div key={sec.section} data-testid={`full-terms-section-${sec.section.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 pb-1 mb-2">
                  {sec.section}
                </div>
                <div className="space-y-1">
                  {sec.clauses.map(c => {
                    const rawVal = c.get(proposal);
                    const formatted = c.format ? c.format(rawVal, proposal) : rawVal;
                    const isEmpty = rawVal === undefined || rawVal === null || rawVal === '' || rawVal === 0 || rawVal === false;
                    const display = isEmpty ? 'Not specified' : formatted;
                    const objCount = pendingObjections.filter(o => o.clause_path === c.key).length;
                    return (
                      <div key={c.key} className="flex items-start justify-between p-2 rounded-md hover:bg-slate-50 transition-colors group">
                        <div className="flex-1 min-w-0 pr-3">
                          <p className="text-xs font-medium text-slate-700">{c.label}</p>
                          <p className={`text-xs mt-0.5 break-words capitalize ${isEmpty ? 'text-slate-400 italic' : 'text-slate-600'}`}>
                            {String(display).replace(/_/g, ' ')}
                          </p>
                        </div>
                        {canRespond && (
                          <Button size="sm" variant="ghost"
                            onClick={() => openRequestChange(c)}
                            data-testid={`request-change-${c.key.replace(/\./g, '-')}`}
                            className="text-[10px] h-6 px-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 shrink-0">
                            <Edit3 className="w-3 h-3 mr-0.5" />
                            {objCount > 0 ? `Pending (${objCount})` : 'Request Change'}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending objections summary */}
      {pendingObjections.length > 0 && (
        <div className="bg-purple-50/50 rounded-2xl border border-purple-200 p-5 mb-5" data-testid="pending-objections-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-purple-800 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" /> {pendingObjections.length} Change{pendingObjections.length === 1 ? '' : 's'} Pending
            </h3>
            <Button size="sm" onClick={() => setCounterOpen(true)} data-testid="review-and-send-counter"
              className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-8">
              <Send className="w-3 h-3 mr-1" /> Review & Send Counter
            </Button>
          </div>
          <div className="space-y-2">
            {pendingObjections.map((o, i) => (
              <div key={i} className="p-3 bg-white rounded-lg border border-purple-100">
                <div className="flex justify-between items-start mb-1">
                  <p className="text-xs font-semibold text-slate-700">{o.clause_label}</p>
                  <button onClick={() => setPendingObjections(pendingObjections.filter((_, j) => j !== i))}
                    className="text-slate-400 hover:text-red-600 text-xs">✕</button>
                </div>
                <p className="text-[10px] text-slate-500">Currently: <span className="capitalize">{String(o.current_value || '—').replace(/_/g, ' ')}</span></p>
                {o.requested_value && <p className="text-[11px] text-purple-700 mt-1">Requested: <span className="font-medium">{o.requested_value}</span></p>}
                {o.note && <p className="text-[11px] text-slate-600 mt-1 italic">"{o.note}"</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Counter History */}
      {deal.counter_history?.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-5" data-testid="counter-history-block">
          <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <History className="w-4 h-4 text-slate-500" /> Counter History ({deal.counter_history.length})
          </h3>
          <div className="space-y-2">
            {deal.counter_history.map((c, i) => (
              <div key={i} className="p-3 bg-slate-50 rounded-lg">
                <div className="flex justify-between mb-1">
                  <span className="text-xs font-semibold text-slate-700">Counter #{i + 1}</span>
                  <span className="text-[10px] text-slate-400">{c.countered_at ? new Date(c.countered_at).toLocaleString() : ''}</span>
                </div>
                {c.counter_price ? <p className="text-xs text-slate-700">Proposed price: ${Number(c.counter_price).toLocaleString()}</p> : null}
                {c.message ? <p className="text-[11px] text-slate-600 mt-1">{c.message}</p> : null}
                {c.clause_objections?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {c.clause_objections.map((o, j) => (
                      <div key={j} className="text-[10px] text-slate-600 pl-2 border-l-2 border-purple-200">
                        <span className="font-medium">{o.clause_label}:</span> {o.requested_value || o.note}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Version history (if any) */}
      {deal.proposal_versions?.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-5">
          <div className="flex items-center justify-between gap-2">
            <button onClick={() => setShowVersions(!showVersions)} type="button"
              data-testid="toggle-versions"
              className="flex items-center justify-between flex-1 text-left">
              <span className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <History className="w-4 h-4 text-slate-500" /> Version History ({deal.proposal_versions.length})
              </span>
              {showVersions ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            {versions.length >= 2 && (
              <Button variant="ghost" size="sm" onClick={() => setDiffViewerOpen(true)}
                data-testid="version-history-compare-btn"
                className="text-xs text-blue-600 hover:bg-blue-50 h-7">
                <GitCompare className="w-3.5 h-3.5 mr-1" /> Compare
              </Button>
            )}
          </div>
          {showVersions && (
            <div className="space-y-2 mt-3">
              {[...deal.proposal_versions].reverse().map((v, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-md border border-slate-100">
                  <div>
                    <p className="text-xs font-semibold text-slate-700">Version {v.version}</p>
                    <p className="text-[10px] text-slate-400">{v.snapshot_at ? new Date(v.snapshot_at).toLocaleString() : ''} · {v.action}</p>
                  </div>
                  <Badge variant="outline" className="text-[9px] capitalize">{v.action?.replace(/_/g, ' ')}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ProposalDiffViewer
        open={diffViewerOpen}
        onClose={() => setDiffViewerOpen(false)}
        versions={versions}
        currentProposal={proposal}
      />

      {/* Action bar */}
      {canRespond && (
        <div className="flex flex-wrap gap-2 sticky bottom-4 bg-white/80 backdrop-blur-sm p-3 rounded-xl border border-slate-200 shadow-sm">
          <Button onClick={startAccept} disabled={responding} data-testid="accept-proposal-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <CheckCircle className="w-4 h-4 mr-2" /> Accept Proposal
          </Button>
          <Button variant="outline" onClick={() => setCounterOpen(true)} disabled={responding}
            data-testid="open-counter-btn"
            className="border-purple-200 text-purple-700 hover:bg-purple-50">
            <MessageSquare className="w-4 h-4 mr-2" />
            Counter{pendingObjections.length > 0 ? ` (${pendingObjections.length} change${pendingObjections.length === 1 ? '' : 's'})` : ''}
          </Button>
          <Button variant="outline" onClick={reject} disabled={responding}
            data-testid="decline-proposal-btn" className="border-red-200 text-red-600 hover:bg-red-50">
            <XCircle className="w-4 h-4 mr-2" /> Decline
          </Button>
        </div>
      )}

      {/* ── Request Change Dialog ── */}
      <Dialog open={requestChangeOpen} onOpenChange={setRequestChangeOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="request-change-dialog">
          <DialogHeader>
            <DialogTitle>Request Change</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {activeClause?.label}
            </DialogDescription>
          </DialogHeader>
          {activeClause && (
            <div className="space-y-3">
              <div className="p-2.5 bg-slate-50 rounded-md text-xs">
                <p className="text-slate-400 mb-1">Currently</p>
                <p className="text-slate-800 capitalize">{String(activeClause.current_value || '—').replace(/_/g, ' ')}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Requested change</Label>
                <Input data-testid="requested-value-input"
                  value={activeClause.requested_value || ''}
                  onChange={e => setActiveClause(p => ({ ...p, requested_value: e.target.value }))}
                  placeholder="What value would you prefer?" className="text-xs" />
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Reason / note (optional)</Label>
                <Textarea data-testid="objection-note-input"
                  value={activeClause.note || ''}
                  onChange={e => setActiveClause(p => ({ ...p, note: e.target.value }))}
                  rows={2} placeholder="Explain why you'd like this changed" className="text-xs" />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" size="sm" onClick={() => setRequestChangeOpen(false)}>Cancel</Button>
                <Button size="sm" onClick={submitClauseChange} data-testid="add-objection-btn"
                  className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus className="w-3 h-3 mr-1" /> Add to Counter
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Counter Review Dialog ── */}
      <Dialog open={counterOpen} onOpenChange={setCounterOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="counter-review-dialog">
          <DialogHeader>
            <DialogTitle>Send Counter</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {pendingObjections.length > 0
                ? `Sending ${pendingObjections.length} change request${pendingObjections.length === 1 ? '' : 's'}.`
                : 'Add an overall counter price or message.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Overall counter price ($, optional)</Label>
              <Input data-testid="counter-price-input" type="number" value={counterPrice}
                onChange={e => setCounterPrice(e.target.value)}
                placeholder={String(headlineAmount)} className="text-xs" />
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Message to agency (optional)</Label>
              <Textarea data-testid="counter-message-input" value={counterMessage}
                onChange={e => setCounterMessage(e.target.value)}
                rows={3} placeholder="Add overall context for your counter" className="text-xs" />
            </div>
            {pendingObjections.length > 0 && (
              <div className="p-3 bg-purple-50/40 rounded-md border border-purple-100 max-h-44 overflow-auto">
                <p className="text-[10px] text-purple-700 font-semibold mb-1.5">Specific change requests</p>
                {pendingObjections.map((o, i) => (
                  <div key={i} className="text-[10px] text-slate-700 py-1 border-b border-purple-100 last:border-0">
                    <span className="font-medium">{o.clause_label}</span>
                    {o.requested_value ? `: → ${o.requested_value}` : ''}
                    {o.note ? ` (${o.note})` : ''}
                  </div>
                ))}
              </div>
            )}
            <Button onClick={sendCounter} disabled={responding} data-testid="send-counter-btn"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white">
              <Send className="w-4 h-4 mr-2" />
              {responding ? 'Sending…' : 'Send Counter to Agency'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Acceptance Acknowledgment Dialog ── */}
      <Dialog open={acceptOpen} onOpenChange={setAcceptOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="accept-acks-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-4 h-4" /> Confirm Sensitive Permissions
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              This proposal includes high-sensitivity AI alterations. Please acknowledge each one explicitly before accepting.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {requiredAcks.map(a => (
              <label key={a.key} className="flex items-start gap-2 p-3 rounded-md border border-amber-100 bg-amber-50/40 hover:bg-amber-50 cursor-pointer">
                <input type="checkbox" data-testid={`ack-${a.key}`}
                  checked={!!acks[a.key]}
                  onChange={e => setAcks(p => ({ ...p, [a.key]: e.target.checked }))}
                  className="rounded border-amber-300 mt-0.5" />
                <span className="text-xs text-slate-800">I explicitly acknowledge that the agency may apply <strong>{a.label.toLowerCase()}</strong> to my likeness within the agreed campaign parameters.</span>
              </label>
            ))}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => setAcceptOpen(false)}>Cancel</Button>
              <Button size="sm" data-testid="confirm-accept-btn"
                disabled={!allAcksChecked || responding}
                onClick={() => doAccept(Object.keys(acks).filter(k => acks[k]))}
                className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <CheckCircle className="w-3 h-3 mr-1" /> Confirm & Accept
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Likeness pack request panel (kept for context) */}
      {(lr.standard_pack_included || lr.additional_requirements?.length || lr.custom_notes) && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-600" /> Likeness Pack Request
          </h3>
          <div className="text-xs space-y-2">
            <div className="flex justify-between"><span className="text-slate-500">Standard Pack</span><span className="text-slate-800 font-medium">{lr.standard_pack_included !== false ? 'Included' : 'Not requested'}</span></div>
            {lr.additional_requirements?.length > 0 && (
              <div>
                <p className="text-slate-500 mb-1">Additional</p>
                <div className="flex flex-wrap gap-1">{lr.additional_requirements.map(r => <Badge key={r} variant="outline" className="text-[10px] capitalize">{r.replace(/_/g, ' ')}</Badge>)}</div>
              </div>
            )}
            {lr.custom_notes && <div><p className="text-slate-500 mb-1">Notes</p><p className="text-slate-800">{lr.custom_notes}</p></div>}
          </div>
        </div>
      )}
    </div>
  );
}
